'use client';

import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { Globe, WarningCircle } from '@phosphor-icons/react';

type BrowserLiveState = 'active' | 'deleted' | 'unknown';

interface BrowserLiveViewRendererProps {
  input?: Record<string, unknown>;
  output?: unknown;
  displayContext?: 'inline' | 'artifact';
  /** Chat-view supplied affordance (e.g. an Expand button) shown on the inline chip. */
  renderActions?: ReactNode;
}

function getString(value: unknown) {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function getBrowserName(output: unknown) {
  if (!output || typeof output !== 'object') return undefined;
  const out = output as { title?: unknown; browser?: { name?: unknown } };
  return getString(out.title) ?? getString(out.browser?.name);
}

function getLiveViewUrl(output: unknown) {
  if (!output || typeof output !== 'object') return undefined;
  const out = output as {
    liveViewUrl?: unknown;
    browser?: { liveViewUrl?: unknown; browser_live_view_url?: unknown };
  };
  return getString(out.liveViewUrl)
    ?? getString(out.browser?.liveViewUrl)
    ?? getString(out.browser?.browser_live_view_url);
}

function getCurrentUrl(input: Record<string, unknown> | undefined, output: unknown) {
  if (output && typeof output === 'object') {
    const out = output as { url?: unknown; currentUrl?: unknown; browser?: { url?: unknown; startUrl?: unknown } };
    const fromOutput = getString(out.currentUrl) ?? getString(out.url)
      ?? getString(out.browser?.url) ?? getString(out.browser?.startUrl);
    if (fromOutput) return fromOutput;
  }
  return getString(input?.url) ?? getString(input?.startUrl);
}

/** Galexia browser id (preferred) or Kernel provider id — whichever the tool
 *  output carries. Used to poll the liveness endpoint. */
function getBrowserId(output: unknown): string | undefined {
  if (!output || typeof output !== 'object') return undefined;
  const out = output as {
    browserId?: unknown;
    browser?: { id?: unknown; providerBrowserId?: unknown };
  };
  return getString(out.browser?.id)
    ?? getString(out.browser?.providerBrowserId)
    ?? getString(out.browserId);
}

/** Best-effort initial state from the tool output so the badge isn't blank
 *  before the first poll resolves. Unknown when the output predates state. */
function getInitialState(output: unknown): BrowserLiveState {
  if (!output || typeof output !== 'object') return 'unknown';
  const out = output as { browser?: { state?: unknown; deletedAt?: unknown } };
  const state = getString(out.browser?.state);
  if (out.browser?.deletedAt || state === 'deleted') return 'deleted';
  if (state === 'active') return 'active';
  return 'unknown';
}

/** Honest liveness badge — green LIVE (pulsing) while active, neutral ENDED
 *  once the session is gone, neutral label when state is unknown. */
function StatusBadge({ state }: { state: BrowserLiveState }) {
  if (state === 'active') {
    return (
      <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/[0.08] px-2 py-1 text-[10px] font-bold uppercase tracking-[2px] text-emerald-300">
        <span className="relative flex size-1.5">
          <span className="absolute inline-flex size-full animate-ping rounded-full bg-emerald-400/70" />
          <span className="relative inline-flex size-1.5 rounded-full bg-emerald-400" />
        </span>
        Live
      </span>
    );
  }
  if (state === 'deleted') {
    return (
      <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-border/60 bg-muted/40 px-2 py-1 text-[10px] font-bold uppercase tracking-[2px] text-muted-foreground">
        <span className="size-1.5 rounded-full bg-muted-foreground/60" />
        Ended
      </span>
    );
  }
  return (
    <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-border/60 bg-muted/30 px-2 py-1 text-[10px] font-bold uppercase tracking-[2px] text-muted-foreground">
      <span className="size-1.5 rounded-full bg-muted-foreground/50" />
      Browser
    </span>
  );
}

/**
 * Polls the liveness endpoint to keep the badge honest.
 * - Fetches once on mount always; in the artifact panel it then re-polls every
 *   25s (the live view is on screen). Inline chips poll only once.
 * - A poll FAILURE (network/404) leaves the last-known state untouched — the
 *   badge never flips to ENDED on a transient error.
 * - Once the browser reports 'deleted', polling stops permanently.
 */
function useBrowserLiveness(
  browserId: string | undefined,
  initialState: BrowserLiveState,
  displayContext: 'inline' | 'artifact',
) {
  const [state, setState] = useState<BrowserLiveState>(initialState);
  const [polledUrl, setPolledUrl] = useState<string | undefined>(undefined);
  const [polledLiveViewUrl, setPolledLiveViewUrl] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!browserId) return;
    let cancelled = false;
    let interval: ReturnType<typeof setInterval> | null = null;

    const stopInterval = () => {
      if (interval) {
        clearInterval(interval);
        interval = null;
      }
    };

    const poll = async () => {
      try {
        const res = await fetch(`/api/browsers/${encodeURIComponent(browserId)}`, { cache: 'no-store' });
        if (!res.ok) return; // keep last-known state on 404/5xx
        const json = (await res.json()) as { state?: string; currentUrl?: string; liveViewUrl?: string };
        if (cancelled) return;
        if (json.state === 'active' || json.state === 'deleted') {
          setState(json.state);
          if (json.state === 'deleted') stopInterval();
        }
        if (json.currentUrl) setPolledUrl(json.currentUrl);
        if (json.liveViewUrl) setPolledLiveViewUrl(json.liveViewUrl);
      } catch {
        // Network error — keep the last-known state, never flip to ENDED.
      }
    };

    poll();
    if (displayContext === 'artifact' && initialState !== 'deleted') {
      interval = setInterval(poll, 25_000);
    }

    return () => {
      cancelled = true;
      stopInterval();
    };
  }, [browserId, displayContext, initialState]);

  return { state, polledUrl, polledLiveViewUrl };
}

export function BrowserLiveViewRenderer({
  input,
  output,
  displayContext = 'inline',
  renderActions,
}: BrowserLiveViewRendererProps) {
  const browserId = getBrowserId(output);
  const initialState = getInitialState(output);
  const { state, polledUrl, polledLiveViewUrl } = useBrowserLiveness(browserId, initialState, displayContext);

  const liveViewUrl = polledLiveViewUrl ?? getLiveViewUrl(output);
  const browserName = getBrowserName(output) ?? getString(input?.name) ?? 'Browser';
  const displayUrl = polledUrl ?? getCurrentUrl(input, output);

  // INLINE: never embed an iframe. A compact single-row chip that reflects
  // liveness and (via renderActions) can expand into the artifact panel.
  if (displayContext !== 'artifact') {
    return (
      <div className="flex items-center gap-2.5 rounded-lg border border-border bg-background/60 px-3 py-2">
        <span className="inline-flex size-7 shrink-0 items-center justify-center rounded-md border border-border/60 bg-muted/30">
          <Globe weight="duotone" className="size-4 text-muted-foreground" />
        </span>
        <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
          {displayUrl ?? 'Browser session'}
        </span>
        <StatusBadge state={state} />
        {renderActions}
      </div>
    );
  }

  // ARTIFACT: the ONE place the live view renders as an iframe.
  const topBar = (
    <div className="flex min-h-11 shrink-0 items-center gap-3 border-b border-border/60 bg-background/80 px-3 backdrop-blur-md">
      <div className="flex min-w-0 flex-1 items-center gap-2 rounded-full border border-border/70 bg-muted/30 px-3 py-1.5">
        <Globe weight="duotone" className="size-3.5 shrink-0 text-muted-foreground" />
        <span className="truncate text-xs text-muted-foreground">
          {displayUrl ?? 'Browser session'}
        </span>
      </div>
      <StatusBadge state={state} />
    </div>
  );

  // Ended sessions: don't embed a dead live-view URL — show a muted placeholder.
  if (state === 'deleted') {
    return (
      <div className="flex h-full min-h-0 w-full flex-col overflow-hidden bg-background">
        {topBar}
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-2 p-6 text-center">
          <Globe weight="duotone" className="size-8 text-muted-foreground/50" />
          <p className="text-sm font-medium text-muted-foreground">Browser session ended</p>
          <p className="max-w-xs text-xs text-muted-foreground/70">
            This browser has been closed. Create a new browser to continue.
          </p>
        </div>
      </div>
    );
  }

  if (!liveViewUrl) {
    return (
      <div className="rounded-lg border border-amber-500/20 bg-amber-500/[0.04] p-4">
        <div className="flex items-start gap-3">
          <WarningCircle weight="duotone" className="mt-0.5 size-5 text-amber-500" />
          <div className="min-w-0">
            <p className="text-sm font-medium">No live browser view available</p>
            <p className="mt-1 text-sm text-muted-foreground">
              The browser may be headless, expired, or missing a live-view URL.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 w-full flex-col overflow-hidden bg-black">
      {topBar}
      <iframe
        title={browserName}
        src={liveViewUrl}
        className="min-h-0 w-full flex-1 border-0 bg-black"
        allow="clipboard-read; clipboard-write; fullscreen"
      />
    </div>
  );
}
